import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Role } from '../auth/api';
import {
  archiveUser,
  clearMfa,
  createUser,
  listUsers,
  resetPassword,
  revokeUserSessions,
  unlockUser,
  updateUser,
  type AdminUser,
} from './api';

const ROLES: Role[] = ['viewer', 'operator', 'engineer', 'manager', 'admin'];

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function NewUser({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createUser({
        email: email.trim(),
        display_name: name.trim(),
        password,
        role,
        locale: 'ru',
        mfa_required: mfaRequired,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onDone();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.code : 'unknown'),
  });

  return (
    <div className="admin__form">
      <h3>{t('users.add')}</h3>

      <label htmlFor="u-email">{t('users.email')}</label>
      <input id="u-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />

      <label htmlFor="u-name">{t('users.name')}</label>
      <input id="u-name" value={name} onChange={(e) => setName(e.target.value)} />

      <label htmlFor="u-pass">{t('users.password')}</label>
      <input
        id="u-pass"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <p className="admin__hint">{t('users.passwordHint')}</p>

      <label htmlFor="u-role">{t('users.role')}</label>
      <select id="u-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
        {ROLES.map((value) => (
          <option key={value} value={value}>
            {t(`roles.${value}`)}
          </option>
        ))}
      </select>

      <label className="admin__check">
        <input
          type="checkbox"
          checked={mfaRequired}
          onChange={(e) => setMfaRequired(e.target.checked)}
        />
        {t('users.requireMfa')}
      </label>

      {error && (
        <p className="security__warning" role="alert">
          {t(`users.errors.${error}`, { defaultValue: t('users.errors.unknown') })}
        </p>
      )}

      <div className="admin__actions">
        <button
          type="button"
          className="primary"
          onClick={() => create.mutate()}
          disabled={create.isPending || !email || !name || password.length < 10}
        >
          {t('common.save')}
        </button>
        <button type="button" onClick={onDone}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

function UserRow({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['users'] });
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.code : 'unknown');

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof updateUser>[1]) => updateUser(user.id, body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });
  const reset = useMutation({
    mutationFn: () => resetPassword(user.id, password),
    onSuccess: () => {
      setPassword('');
      setError(null);
      refresh();
    },
    onError: fail,
  });
  const stripMfa = useMutation({ mutationFn: () => clearMfa(user.id), onSuccess: refresh });
  const unlock = useMutation({ mutationFn: () => unlockUser(user.id), onSuccess: refresh });
  const signOutAll = useMutation({
    mutationFn: () => revokeUserSessions(user.id),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: () => archiveUser(user.id),
    onSuccess: refresh,
    onError: fail,
  });

  const locked = user.locked_until && new Date(user.locked_until) > new Date();
  const isMe = me?.id === user.id;

  return (
    <>
      <tr className={user.archived_at ? 'is-archived' : undefined}>
        <td>
          <button type="button" className="link" onClick={() => setOpen((value) => !value)}>
            {user.display_name}
          </button>
          <div className="admin__sub">{user.email}</div>
        </td>
        <td>{t(`roles.${user.role}`)}</td>
        <td>
          {user.archived_at
            ? t('users.state.archived')
            : locked
              ? t('users.state.locked')
              : user.is_active
                ? t('users.state.active')
                : t('users.state.disabled')}
        </td>
        <td>
          {user.totp_enrolled && <span className="badge badge--on">{t('users.totp')}</span>}
          {user.passkey_count > 0 && (
            <span className="badge">
              {t('users.keys', { count: user.passkey_count })}
            </span>
          )}
          {user.mfa_required && !user.totp_enrolled && (
            <span className="badge badge--warn">{t('users.mfaPending')}</span>
          )}
        </td>
        <td>{user.active_sessions}</td>
        <td>{when(user.last_login_at)}</td>
      </tr>

      {open && (
        <tr className="admin__detail-row">
          <td colSpan={6}>
            <div className="admin__detail">
              <div className="admin__group">
                <label htmlFor={`role-${user.id}`}>{t('users.role')}</label>
                <select
                  id={`role-${user.id}`}
                  value={user.role}
                  onChange={(e) => patch.mutate({ role: e.target.value as Role })}
                >
                  {ROLES.map((value) => (
                    <option key={value} value={value}>
                      {t(`roles.${value}`)}
                    </option>
                  ))}
                </select>

                <label className="admin__check">
                  <input
                    type="checkbox"
                    checked={user.is_active}
                    disabled={isMe}
                    onChange={(e) => patch.mutate({ is_active: e.target.checked })}
                  />
                  {t('users.active')}
                </label>

                <label className="admin__check">
                  <input
                    type="checkbox"
                    checked={user.mfa_required}
                    onChange={(e) => patch.mutate({ mfa_required: e.target.checked })}
                  />
                  {t('users.requireMfa')}
                </label>
              </div>

              <div className="admin__group">
                <label htmlFor={`pw-${user.id}`}>{t('users.resetPassword')}</label>
                <input
                  id={`pw-${user.id}`}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {/* Said up front: an administrator resetting a password is
                    usually doing it because it leaked, and leaving the intruder
                    signed in would make the reset cosmetic. */}
                <p className="admin__hint">{t('users.resetEndsSessions')}</p>
                <button
                  type="button"
                  onClick={() => reset.mutate()}
                  disabled={password.length < 10}
                >
                  {t('users.resetPassword')}
                </button>
              </div>

              <div className="admin__group">
                <button type="button" onClick={() => stripMfa.mutate()}>
                  {t('users.clearMfa')}
                </button>
                <p className="admin__hint">{t('users.clearMfaHint')}</p>

                {locked && (
                  <button type="button" onClick={() => unlock.mutate()}>
                    {t('users.unlock')}
                  </button>
                )}
                <button type="button" onClick={() => signOutAll.mutate()}>
                  {t('users.signOutEverywhere')}
                </button>
                {!isMe && !user.archived_at && (
                  <button type="button" className="danger" onClick={() => archive.mutate()}>
                    {t('users.archive')}
                  </button>
                )}
              </div>

              {error && (
                <p className="security__warning" role="alert">
                  {t(`users.errors.${error}`, { defaultValue: t('users.errors.unknown') })}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function UsersPage() {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);

  const users = useQuery({
    queryKey: ['users', showArchived],
    queryFn: () => listUsers(showArchived),
  });

  return (
    <div className="admin">
      <div className="admin__bar">
        <h2>{t('nav.users')}</h2>
        <label className="admin__check">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          {t('users.showArchived')}
        </label>
        <button type="button" onClick={() => setAdding((value) => !value)}>
          {t('users.add')}
        </button>
      </div>

      {adding && <NewUser onDone={() => setAdding(false)} />}

      <div className="scroll-x">
        <table className="admin__table">
          <thead>
            <tr>
              <th>{t('users.name')}</th>
              <th>{t('users.role')}</th>
              <th>{t('users.status')}</th>
              <th>{t('users.factors')}</th>
              <th>{t('users.sessions')}</th>
              <th>{t('users.lastLogin')}</th>
            </tr>
          </thead>
          <tbody>
            {(users.data ?? []).map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
